/**
 * SwIdleReload (Plan 01-15, Task 3).
 *
 * UI-SPEC §"Interaction Contracts §4 — Tablet (always-on)":
 *   When `hasUpdate && isIdle && deviceClass === 'tablet'`:
 *     - render full-screen overlay fading from 1 → 0 over 500ms
 *     - center-bottom toast `t('sw.update.tablet_toast')` (`bg-sheet`,
 *       `text-ink-2`, shadow-sm, 11px label per UI-SPEC)
 *     - after 500ms (or 0ms under `prefers-reduced-motion`), call
 *       `sendSkipWaiting(reg)`. Plan 01-08 `controllerchange` listener fires
 *       `location.reload()` once the new SW takes control.
 *     - if `waitingReg` is null (impossible in normal flow but possible if
 *       the store is mutated externally), fall back to direct reload.
 *
 * The 500ms fade is the polite signal to anyone glancing at the kitchen
 * tablet that "the screen is about to refresh, this is intentional, not
 * a crash". It's brief enough that no one reading the dashboard loses their
 * place, and the reload itself clears the overlay.
 *
 * `useEffect` deps: [hasUpdate, isIdle, deviceClass, waitingReg]. The waiting
 * registration is part of the trigger — if `setUpdate` fires later than the
 * idle/tablet states, the effect re-runs and the timer arms.
 */
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwUpdateStore } from './SwUpdateStore';
import { useDashboard } from '../dashboard/DashboardStore';
import { sendSkipWaiting } from '../../lib/sw-register';

export function SwIdleReload(): ReactElement | null {
  const { t } = useTranslation();
  const hasUpdate = useSwUpdateStore((s) => s.hasUpdate);
  const waitingReg = useSwUpdateStore((s) => s.waitingReg);
  const isIdle = useDashboard((s) => s.isIdle);
  const deviceClass = useDashboard((s) => s.deviceClass);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!hasUpdate || !isIdle || deviceClass !== 'tablet') return;
    const reduce =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
    setFading(true);
    const delay = reduce ? 0 : 500;
    const tid = setTimeout(() => {
      if (waitingReg) {
        sendSkipWaiting(waitingReg);
        // Plan 08 controllerchange handles reload once SW activates.
      } else {
        // Defensive: if for any reason we lost the registration handle,
        // still reload so the kitchen tablet doesn't sit on a stale bundle.
        window.location.reload();
      }
    }, delay);
    return () => clearTimeout(tid);
  }, [hasUpdate, isIdle, deviceClass, waitingReg]);

  // `fading` is a one-way latch with no reset path: once armed, this overlay
  // stays mounted whatever `isIdle` does afterwards. Combined with the static
  // `opacity-0` below and no `pointer-events-none`, that makes it an invisible
  // full-screen hit-test blocker — and since issue #45 it sits on the top rung
  // of the stacking ladder, directly above IdleMode's wake handler. Harmless
  // while the reload it is waiting for actually lands; pre-existing and out of
  // scope for #45, but whoever fixes the fade should fix the latch and the
  // hit-testing in the same pass.
  if (!fading) return null;

  return (
    <div
      // z-70 — this overlay only ever ARMS while `isIdle` is true, so it has
      // to clear IdleMode's z-60 ambient takeover; at z-50 it would fade in
      // behind the hub and never be seen (issue #45; Pattern 7 in
      // docs/ARCHITECTURE.md).
      className="fixed inset-0 z-70 paper-tex transition-opacity duration-500 opacity-0"
      data-testid="sw-idle-reload"
    >
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-sheet text-ink-2 px-4 py-2 rounded-lg shadow-sm text-xs">
        {t('sw.update.tablet_toast')}
      </div>
    </div>
  );
}
